import { css } from '@emotion/react';
import { urls } from '@zapier/identity-utils';
import { Button, Link, TextInput, atoms } from '@zapier/marketing-ui';
import { useEffect, useRef } from 'react';

import {
  LoginData,
  LoginErrors,
  LoginFieldFlags,
  LoginStatus,
} from '../../hooks';
import { useAutoFocus } from '../../hooks/useAutoFocus';

type Props = {
  canSubmit: boolean;
  formData: LoginData;
  formErrorId?: string;
  formErrors: LoginErrors;
  handleChange: (field: string, value: string) => void;
  handleSubmit: () => void;
  shouldDisableEmailField: boolean;
  shouldFocusEmailField: boolean;
  shouldShowEmailField: boolean;
  shouldShowPasswordField: boolean;
  status: LoginStatus;
  submittedFields: LoginFieldFlags;
};

const styles = {
  field: css`
    margin-bottom: 20px;
  `,

  // Hide the fields in a very "creative" way so that
  // password managers still consider them visible and
  // fill them in. `display: none` doesn't work for all
  // password managers.
  hidden: css`
    margin-bottom: 0;
    position: relative;
    overflow: hidden;

    * {
      position: absolute !important;
      left: 100vw !important;
    }
  `,

  password: css`
    margin-bottom: 8px;
  `,
};

export function EmailPasswordForm({
  canSubmit,
  formData,
  formErrorId,
  formErrors,
  handleChange,
  handleSubmit,
  shouldDisableEmailField,
  shouldFocusEmailField,
  shouldShowEmailField,
  shouldShowPasswordField,
  status,
  submittedFields,
}: Props) {
  // Used to focus email field when we first switch to the login page
  const emailRef = useAutoFocus(shouldShowEmailField && shouldFocusEmailField);

  // Used to focus password field when we first switch to the password page
  const passwordRef = useAutoFocus(shouldShowPasswordField);

  const submitRef = useRef<HTMLButtonElement>(null);

  const emailInError = Boolean(
    status === 'error' && submittedFields?.email && formErrors.email
  );
  const passwordInError = Boolean(
    status === 'error' && submittedFields?.password && formErrors.password
  );
  const formInError = Boolean(status === 'error' && formErrors.error);

  useEffect(() => {
    if (!emailInError && !passwordInError && !formInError) {
      return;
    }

    // Submit drops focus to <body>; refocus the visible field (SC 2.4.3).
    const field = shouldShowPasswordField
      ? passwordRef.current
      : emailRef.current;
    // A disabled (invitation) field can't take focus; fall back to the submit button.
    const target = field?.disabled ? submitRef.current : field;

    target?.focus();
    // Stable while typing, so focus is not yanked mid-edit.
  }, [
    emailInError,
    passwordInError,
    formInError,
    shouldShowPasswordField,
    emailRef,
    passwordRef,
  ]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        css={[styles.field, !shouldShowEmailField && styles.hidden]}
        aria-hidden={!shouldShowEmailField}
        // `inert` removes the offscreen-hidden subtree from the focus order
        // entirely, so the `aria-hidden` subtree contains no focusable
        // descendants (satisfies axe's `aria-hidden-focus` rule). `|| undefined`
        // ensures the attribute is omitted (not rendered as `inert="false"`)
        // when the field is shown.
        inert={!shouldShowEmailField || undefined}
        data-testid="emailField-wrapper"
      >
        <TextInput
          ref={emailRef}
          id="login-email"
          name="email"
          label="Email"
          type="email"
          placeholder="Email"
          value={formData.email}
          required={true}
          disabled={shouldDisableEmailField || status === 'submitting'}
          error={
            status === 'error' && submittedFields?.email ? formErrors.email : ''
          }
          autoComplete="email"
          inputMode="email"
          tabIndex={shouldShowEmailField ? undefined : -1}
          onChange={value => handleChange('email', value)}
        />
      </div>
      <div
        css={[styles.field, !shouldShowPasswordField && styles.hidden]}
        aria-hidden={!shouldShowPasswordField}
        inert={!shouldShowPasswordField || undefined}
        data-testid="passwordField-wrapper"
      >
        <TextInput
          css={styles.password}
          ref={passwordRef}
          id="login-password"
          name="password"
          label="Password"
          type="password"
          placeholder="Password"
          value={formData.password}
          required={Boolean(shouldShowPasswordField)}
          disabled={status === 'submitting' || status === 'success'}
          error={
            status === 'error' && submittedFields?.password
              ? formErrors.password
              : ''
          }
          autoComplete="current-password"
          tabIndex={shouldShowPasswordField ? undefined : -1}
          onChange={value => handleChange('password', value)}
        />
        <div className={`${atoms.bodySM} ${atoms.textNeutral800}`}>
          <Link href={urls.FORGOT_PASSWORD_URL}>Forgot Password?</Link>
        </div>
      </div>
      <Button
        ref={submitRef}
        // Focus can land here on failure and drown out the alert in VoiceOver, so the button carries the error too.
        aria-describedby={formErrorId}
        fullWidth={true}
        variant="brand"
        disabled={!canSubmit || status === 'submitting' || status === 'success'}
        type="submit"
        id="login-submit"
      >
        <span data-testid="email-password-continue">Continue</span>
      </Button>
    </form>
  );
}
